import vr from "../../../utilFunctions/validationRules";

const {
    lenBetween,
    isPositiveInt,
    isOptionalArrayOfIds,
    isCustomFields,
    isString,
} = vr;

export const isEmpty = (v) =>
    v == null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

const isValidCustomField = (field) => {
    if (!field) return true;

    const labelValid = !field.label || lenBetween(field.label, 1, 100);
    const valueValid = !field.value || lenBetween(field.value, 1, 255);

    if (field.label || field.value) {
        return labelValid && valueValid;
    }

    return true;
};


export const buildLocationValidation = (t) => ({
    name: {
        required: true,
        requiredMessage: t?.common?.required_field,
        invalidMessage:
            t?.common?.minmax_chars?.replace?.("{min}", "3")?.replace?.("{max}", "150"),
        pattern: (v) => lenBetween(v, 3, 150),
        error: true,
        skipped: false,
    },
    address: {
        required: true,
        requiredMessage: t?.common?.required_field || "Required",
        invalidMessage:
            t?.common?.minmax_chars?.replace?.("{min}", "3")?.replace?.("{max}", "250"),
        pattern: (v) => lenBetween(v, 3, 250),
        error: true,
        skipped: false,
    },
    country: {
        required: true,
        requiredMessage: t?.common?.required_field,
        invalidMessage:
            t?.common?.minmax_chars?.replace?.("{min}", "2")?.replace?.("{max}", "50"),
        pattern: (v) => lenBetween(v, 2, 50),
        error: true,
        skipped: false,
    },
    city: {
        required: true,
        requiredMessage: t?.common?.required_field || "Required",
        invalidMessage:
            t?.common?.minmax_chars?.replace?.("{min}", "2")?.replace?.("{max}", "50"),
        pattern: (v) => lenBetween(v, 2, 50),
        error: true,
        skipped: false,
    },
    image: {
        required: false,
        invalidMessage: t?.common?.invalid,
        pattern: (v) => v == null || isPositiveInt(Number(v)),
        error: false,
        skipped: true,
    },
    parentLocation: {
        required: false,
        invalidMessage: t?.common?.invalid,
        pattern: (v) => v == null || isPositiveInt(Number(v)),
        error: false,
        skipped: true,
    },
    procedure: {
        required: false,
        invalidMessage: t?.common?.invalid || "Invalid value",
        pattern: (v) => v == null || isPositiveInt(Number(v)),
        error: false,
        skipped: true,
    },
    area: {
        required: false,
        invalidMessage: t?.common?.invalid,
        pattern: (v) =>
            v == null || (Array.isArray(v) && v.every((a) => !a?.area || isString(a.area))),
        error: false,
        skipped: true,
    },
    createdByUser: {
        required: false,
        invalidMessage: t?.common?.invalid ,
        pattern: (v) => v == null || isPositiveInt(Number(v)),
        error: false,
        skipped: true,
    },
    customer: {
        required: false,
        invalidMessage: t?.common?.invalid,
        pattern: (arr) => arr == null || isOptionalArrayOfIds(arr),
        error: false,
        skipped: true,
    },
    users: {
        required: false,
        invalidMessage: t?.common?.invalid,
        pattern: (arr) => arr == null || isOptionalArrayOfIds(arr),
        error: false,
        skipped: true,
    },
    teams: {
        required: false,
        invalidMessage: t?.common?.invalid,
        pattern: (arr) => arr == null || isOptionalArrayOfIds(arr),
        error: false,
        skipped: true,
    },
    vendors: {
        required: false,
        invalidMessage: t?.common?.invalid,
        pattern: (arr) => arr == null || isOptionalArrayOfIds(arr),
        error: false,
        skipped: true,
    },
    customFields: {
        required: false,
        invalidMessage: t?.common?.invalid,
        pattern: (arr) => {
            if (arr == null || !Array.isArray(arr)) return true;
            return arr.every(field => isValidCustomField(field));
        },
        error: false,
        skipped: true,
    },
});

export const computeValidation = (value, rule, requiredFallback = "Required") => {
    const empty = isEmpty(value);
    const passes = rule.pattern ? rule.pattern(value) : true;

    const error = rule.required ? (empty || (!empty && !passes)) : (!empty && !passes);
    const message =
        empty && rule.required
            ? rule.requiredMessage ?? requiredFallback
            : !passes
                ? rule.invalidMessage ?? rule.message ?? "Invalid value"
                : "";

    return { error, message, skipped: true };
};


export const makeOnBlurFactory = ({ getData, setValidation }) => {
    return (key, options = {}) => () => {
        const { valueSelector = (d) => d[key], requiredSelector } = options;

        setValidation((prev) => {
            const rule = prev[key];
            if (!rule) return prev;

            const data = getData();
            const val = valueSelector(data);
            const effRule = requiredSelector ? { ...rule, required: !!requiredSelector(data) } : rule;

            return { ...prev, [key]: { ...rule, ...computeValidation(val, effRule) } };
        });
    };
};

export const makeOnValuesChangeFactory = ({
                                              getData,
                                              setData,
                                              setValidation,
                                              vKeyOf = (n) => n,
                                              setInitChange,
                                          }) => {
    return (name, options = {}) => (event, uiData) => {
        const { requiredSelector, valueSelector } = options;
        if (typeof setInitChange === "function") setInitChange(true);

        const dropdownFields = [
            "parentLocation",
            "country",
            "city",
            "users",
            "workers",
            "teams",
            "customer",
            "procedure",
            "vendors",
            "propertyType",
        ];
        const booleanFields = ["isParent"];

        let currentFieldValue = "";

        if (!dropdownFields.includes(name) && !booleanFields.includes(name)) {
            const raw = event?.target?.value ?? "";
            currentFieldValue = valueSelector ? valueSelector(raw, { event, data: uiData }) : raw;
            setData((prev) => ({ ...prev, [name]: currentFieldValue }));
        }

        if (dropdownFields.includes(name)) {
            const raw = uiData?.value;
            currentFieldValue = valueSelector ? valueSelector(raw, { event, data: uiData }) : raw;

            if (name === "country") {
                setData((prev) => ({ ...prev, country: currentFieldValue, city: "" }));
            } else {
                setData((prev) => ({ ...prev, [name]: currentFieldValue }));
            }
        }

        if (booleanFields.includes(name)) {
            const raw = !!uiData?.checked;
            currentFieldValue = valueSelector ? valueSelector(raw, { event, data: uiData }) : raw;
            setData((prev) => ({ ...prev, [name]: currentFieldValue }));
        }

        setValidation((prev) => {
            const key = vKeyOf(name);
            const rule = prev[key];
            if (!rule) return prev;

            const dataSnapshot = getData();
            const effRule = requiredSelector ? { ...rule, required: !!requiredSelector(dataSnapshot) } : rule;
            const res = computeValidation(currentFieldValue, effRule);

            return {
                ...prev,
                [key]: {
                    ...effRule,
                    ...res,
                },
            };
        });
    };
};

export const makeValidateOnSubmitFactory = ({ getData, validation, setValidation, requiredSelectors = {} }) => {
    return () => {
        const d = getData();
        const next = { ...validation };
        const keys = Object.keys(next);

        keys.forEach((k) => {
            const baseRule = next[k];
            if (!baseRule) return;
            const effRule = requiredSelectors[k]
                ? { ...baseRule, required: !!requiredSelectors[k](d) }
                : baseRule;
            const val = d[k];
            const res = computeValidation(val, effRule);
            next[k] = { ...effRule, ...res };
        });

        setValidation(next);
        return keys.some((k) => next[k]?.error);
    };
};
